import { useState, useEffect, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { 
  Building2, Users, Calendar, Vote, ChevronRight, Search, 
  TrendingUp, Clock, MapPin, AlertCircle, RefreshCw, Landmark, User,
  ChevronDown, Filter, Zap, Bell, X, Check
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Mobile-friendly dropdown component
const MobileDropdown = ({ value, onChange, options, placeholder, label }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  
  const filteredOptions = options.filter(opt => 
    opt.label.toLowerCase().includes(searchTerm.toLowerCase())
  );
  
  const selectedLabel = options.find(opt => opt.value === value)?.label || placeholder;
  
  return (
    <>
      {/* Trigger Button */}
      <button
        onClick={() => setIsOpen(true)}
        className="w-full h-10 px-3 border border-slate-200 rounded-lg text-sm bg-white flex items-center justify-between hover:bg-slate-50 transition-colors"
        data-testid={`dropdown-${label?.toLowerCase().replace(/\s/g, '-')}`}
      >
        <span className={value ? 'text-slate-900' : 'text-slate-500'}>
          {selectedLabel}
        </span>
        <ChevronDown className="h-4 w-4 text-slate-400" />
      </button>
      
      {/* Full-screen Modal on Mobile */}
      {isOpen && (
        <div className="fixed inset-0 z-50 bg-white md:bg-black/50 md:flex md:items-center md:justify-center">
          <div className="h-full w-full md:h-auto md:max-h-[80vh] md:w-96 md:rounded-xl bg-white md:shadow-2xl flex flex-col">
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-slate-200 sticky top-0 bg-white z-10">
              <h3 className="font-semibold text-lg">{label || 'Select Option'}</h3>
              <button 
                onClick={() => { setIsOpen(false); setSearchTerm(''); }}
                className="p-2 hover:bg-slate-100 rounded-full transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            
            {/* Search (for lists with many options) */}
            {options.length > 10 && (
              <div className="p-3 border-b border-slate-100">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <input
                    type="text"
                    placeholder="Search..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    autoFocus
                  />
                </div>
              </div>
            )}
            
            {/* Options List */}
            <div className="flex-1 overflow-y-auto">
              {/* Clear selection option */}
              <button
                onClick={() => { onChange(''); setIsOpen(false); setSearchTerm(''); }}
                className={`w-full px-4 py-3 text-left flex items-center justify-between hover:bg-slate-50 transition-colors border-b border-slate-100 ${
                  !value ? 'bg-blue-50 text-blue-700' : ''
                }`}
              >
                <span>{placeholder}</span>
                {!value && <Check className="h-5 w-5 text-blue-600" />}
              </button>
              
              {filteredOptions.map((option) => (
                <button
                  key={option.value}
                  onClick={() => { onChange(option.value); setIsOpen(false); setSearchTerm(''); }}
                  className={`w-full px-4 py-3 text-left flex items-center justify-between hover:bg-slate-50 transition-colors border-b border-slate-100 ${
                    value === option.value ? 'bg-blue-50 text-blue-700' : ''
                  }`}
                >
                  <div className="flex items-center gap-3">
                    {option.color && (
                      <span className={`w-3 h-3 rounded-full ${option.color}`} />
                    )}
                    <span>{option.label}</span>
                  </div>
                  {value === option.value && <Check className="h-5 w-5 text-blue-600" />}
                </button>
              ))}
              
              {filteredOptions.length === 0 && (
                <div className="p-8 text-center text-slate-500">
                  No options found
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

const PARTY_COLORS = {
  labour: { bg: 'bg-red-500', text: 'text-red-500', hex: '#ef4444', label: 'Labour' },
  conservative: { bg: 'bg-blue-600', text: 'text-blue-600', hex: '#2563eb', label: 'Conservative' },
  libdem: { bg: 'bg-orange-500', text: 'text-orange-500', hex: '#f97316', label: 'Lib Dem' },
  green: { bg: 'bg-green-500', text: 'text-green-500', hex: '#22c55e', label: 'Green' },
  independent: { bg: 'bg-gray-500', text: 'text-gray-500', hex: '#6b7280', label: 'Independent' },
  reform: { bg: 'bg-cyan-500', text: 'text-cyan-500', hex: '#06b6d4', label: 'Reform UK' },
  snp: { bg: 'bg-yellow-500', text: 'text-yellow-500', hex: '#eab308', label: 'SNP' },
  plaid: { bg: 'bg-emerald-600', text: 'text-emerald-600', hex: '#059669', label: 'Plaid Cymru' },
  plaid_cymru: { bg: 'bg-emerald-600', text: 'text-emerald-600', hex: '#059669', label: 'Plaid Cymru' },
  dup: { bg: 'bg-red-800', text: 'text-red-800', hex: '#991b1b', label: 'DUP' },
  sinnfein: { bg: 'bg-green-700', text: 'text-green-700', hex: '#15803d', label: 'Sinn Féin' },
  sinn_fein: { bg: 'bg-green-700', text: 'text-green-700', hex: '#15803d', label: 'Sinn Féin' },
  sdlp: { bg: 'bg-green-600', text: 'text-green-600', hex: '#16a34a', label: 'SDLP' },
  alliance: { bg: 'bg-amber-500', text: 'text-amber-500', hex: '#f59e0b', label: 'Alliance' },
  uup: { bg: 'bg-blue-700', text: 'text-blue-700', hex: '#1d4ed8', label: 'UUP' },
  tuv: { bg: 'bg-indigo-700', text: 'text-indigo-700', hex: '#4338ca', label: 'TUV' },
  speaker: { bg: 'bg-purple-600', text: 'text-purple-600', hex: '#9333ea', label: 'Speaker' },
  ukip: { bg: 'bg-purple-500', text: 'text-purple-500', hex: '#a855f7', label: 'UKIP' },
  other: { bg: 'bg-slate-400', text: 'text-slate-400', hex: '#94a3b8', label: 'Other' }
};

// Breaking News Ticker Component
const BreakingNewsTicker = ({ byElections }) => {
  const tickerItems = useMemo(() => {
    if (!byElections || byElections.length === 0) return [];
    return byElections.slice(0, 8).map(be => {
      const daysUntil = Math.ceil((new Date(be.election_date) - new Date()) / (1000 * 60 * 60 * 24));
      return `${be.constituency} • ${daysUntil} days • ${be.region || 'UK'}`;
    });
  }, [byElections]);

  if (tickerItems.length === 0) return null;

  return (
    <div className="bg-gradient-to-r from-red-600 to-red-700 text-white py-2 overflow-hidden mb-6 rounded-lg" data-testid="breaking-news-ticker">
      <div className="flex items-center">
        <div className="flex-shrink-0 bg-white/20 px-4 py-1 flex items-center gap-2">
          <Zap className="h-4 w-4 animate-pulse" />
          <span className="font-bold text-sm uppercase tracking-wider">Upcoming By-Elections</span>
        </div>
        <div className="flex-1 overflow-hidden">
          <div className="animate-ticker flex whitespace-nowrap">
            {[...tickerItems, ...tickerItems].map((item, i) => (
              <span key={i} className="mx-8 text-sm font-medium">{item}</span>
            ))}
          </div>
        </div>
      </div>
      <style>{`
        @keyframes ticker {
          0% { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
        .animate-ticker {
          animation: ticker 30s linear infinite;
        }
        .animate-ticker:hover {
          animation-play-state: paused;
        }
      `}</style>
    </div>
  );
};

const PartyBadge = ({ party, count }) => {
  const colors = PARTY_COLORS[party] || PARTY_COLORS.other;
  return (
    <div className="flex items-center gap-2">
      <div className={`w-3 h-3 rounded-full ${colors.bg}`} />
      <span className="text-sm">{colors.label}: <strong>{count}</strong></span>
    </div>
  );
};

// MP Card Component with improved styling
const MPCard = ({ mp }) => {
  const colors = PARTY_COLORS[mp.party] || PARTY_COLORS.other;
  const slug = mp.name?.toLowerCase().replace(/ /g, '-').replace(/'/g, '') || '';
  return (
    <Link 
      to={`/politician/${slug}`}
      className="bg-white border border-slate-200 rounded-xl p-4 hover:shadow-lg hover:border-slate-300 transition-all group block"
    >
      <div className="flex items-start gap-3">
        <div className={`w-1 h-full min-h-[50px] rounded-full ${colors.bg} group-hover:w-2 transition-all`} />
        <div className="flex-1">
          <h3 className="font-semibold text-slate-900 group-hover:text-blue-600 transition-colors">{mp.name}</h3>
          <p className="text-sm text-slate-600">{mp.constituency}</p>
          <div className="flex items-center gap-2 mt-2">
            <span className={`text-xs px-2 py-0.5 rounded-full ${colors.bg} text-white font-medium`}>
              {colors.label}
            </span>
            <span className="text-xs text-slate-500 flex items-center gap-1">
              <MapPin className="h-3 w-3" />{mp.region}
            </span>
          </div>
        </div>
      </div>
    </Link>
  );
};

const CouncilCard = ({ council }) => {
  const totalSeats = Object.values(council.party_breakdown || {}).reduce((a, b) => a + b, 0) || council.total_seats;
  
  return (
    <Link 
      to={`/council/${encodeURIComponent(council.name)}`}
      className="block bg-white border border-slate-200 rounded-xl p-5 hover:shadow-lg hover:border-slate-300 transition-all cursor-pointer group"
      data-testid={`council-${council.id}`}
    >
      <div className="flex justify-between items-start mb-3">
        <div>
          <h3 className="font-semibold text-lg text-slate-900 group-hover:text-blue-600 transition-colors">{council.name}</h3>
          <p className="text-sm text-slate-500 flex items-center gap-1">
            <MapPin className="h-3 w-3" />
            {council.region} • {council.council_type}
          </p>
        </div>
        <ChevronRight className="h-5 w-5 text-slate-400 group-hover:text-blue-500 group-hover:translate-x-1 transition-all" />
      </div>
      
      {/* Party breakdown bar */}
      {council.party_breakdown && Object.keys(council.party_breakdown).length > 0 && (
        <div className="mb-3">
          <div className="flex h-4 rounded-full overflow-hidden shadow-inner bg-slate-100">
            {Object.entries(council.party_breakdown)
              .sort((a, b) => b[1] - a[1])
              .map(([party, count]) => {
                const colors = PARTY_COLORS[party] || PARTY_COLORS.other;
                const percentage = (count / totalSeats) * 100;
                return (
                  <div 
                    key={party}
                    className={`${colors.bg} transition-all hover:brightness-110`}
                    style={{ width: `${percentage}%` }}
                    title={`${colors.label}: ${count}`}
                  />
                );
              })}
          </div>
        </div>
      )}
      
      <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm">
        {Object.entries(council.party_breakdown || {})
          .sort((a, b) => b[1] - a[1])
          .slice(0, 4)
          .map(([party, count]) => (
            <PartyBadge key={party} party={party} count={count} />
          ))}
      </div>
      
      <div className="mt-3 pt-3 border-t flex justify-between text-sm text-slate-500">
        <span><Users className="h-4 w-4 inline mr-1" />{totalSeats} seats</span>
        <span><Calendar className="h-4 w-4 inline mr-1" />May 2026</span>
      </div>
    </Link>
  );
};

// By-Election Card with urgency styling
const ByElectionCard = ({ election }) => {
  const daysUntil = Math.ceil((new Date(election.election_date) - new Date()) / (1000 * 60 * 60 * 24));
  const isUrgent = daysUntil <= 7;
  const isSoon = daysUntil <= 14;
  const isParliamentary = election.election_type === 'parliamentary';
  
  return (
    <div className={`bg-white border rounded-xl p-5 transition-all hover:shadow-lg ${
      isUrgent ? 'border-red-300 ring-2 ring-red-100' : 
      isSoon ? 'border-amber-200' : 'border-slate-200'
    }`}>
      <div className="flex items-start justify-between">
        <div>
          <div className="flex items-center gap-2 mb-2">
            <span className={`text-xs font-bold px-2 py-1 rounded-full ${
              isParliamentary 
                ? 'bg-purple-100 text-purple-700 border border-purple-200' 
                : 'bg-amber-100 text-amber-700'
            }`}>
              {isParliamentary ? 'PARLIAMENTARY' : 'BY-ELECTION'}
            </span>
            {isUrgent && (
              <span className="text-xs font-bold px-2 py-1 rounded-full bg-red-100 text-red-700 animate-pulse">
                IMMINENT
              </span>
            )}
          </div>
          <h3 className="font-semibold text-lg text-slate-900">{election.constituency}</h3>
          <p className="text-sm text-slate-500">{election.council_name}</p>
          {election.ward && <p className="text-xs text-slate-400">Ward: {election.ward}</p>}
        </div>
        <div className={`text-right p-3 rounded-lg ${
          isUrgent ? 'bg-red-50' : isSoon ? 'bg-amber-50' : 'bg-slate-50'
        }`}>
          <p className={`text-3xl font-bold ${
            isUrgent ? 'text-red-600' : isSoon ? 'text-amber-600' : 'text-slate-900'
          }`}>{daysUntil}</p>
          <p className="text-xs text-slate-500">days</p>
        </div>
      </div>
      
      <div className="mt-4 flex items-center gap-4 text-sm text-slate-600">
        <span className="flex items-center gap-1">
          <Calendar className="h-4 w-4" />
          {new Date(election.election_date).toLocaleDateString('en-GB', { 
            weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' 
          })}
        </span>
        {election.region && (
          <span className="flex items-center gap-1">
            <MapPin className="h-4 w-4" />
            {election.region}
          </span>
        )}
      </div>
      
      {election.cause && (
        <p className="mt-2 text-xs text-slate-500">Cause: {election.cause}</p>
      )}
    </div>
  );
};

// Councillor Card Component
const CouncillorCard = ({ councillor }) => {
  const colors = PARTY_COLORS[councillor.party] || PARTY_COLORS.other;
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4 hover:shadow-lg hover:border-slate-300 transition-all group">
      <div className="flex items-start gap-3">
        <div className={`w-1 h-full min-h-[60px] rounded-full ${colors.bg} group-hover:w-2 transition-all`} />
        <div className="flex-1">
          <h3 className="font-semibold text-slate-900">{councillor.name}</h3>
          <p className="text-sm text-slate-600 font-medium">{councillor.council_name || councillor.council}</p>
          <p className="text-xs text-slate-500">Ward: {councillor.ward}</p>
          <div className="flex items-center gap-2 mt-2 flex-wrap">
            <span className={`text-xs px-2 py-0.5 rounded-full ${colors.bg} text-white font-medium`}>
              {colors.label}
            </span>
            {councillor.region && (
              <span className="text-xs text-slate-500 flex items-center gap-1">
                <MapPin className="h-3 w-3" />{councillor.region}
              </span>
            )}
            {councillor.next_election && (
              <span className="text-xs text-slate-400">
                Next election: {new Date(councillor.next_election).toLocaleDateString('en-GB', { month: 'short', year: 'numeric' })}
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// Stats Card Component
const StatCard = ({ icon: Icon, value, label, color = 'blue' }) => {
  const colorClasses = {
    blue: 'from-blue-500 to-blue-600',
    green: 'from-green-500 to-green-600',
    purple: 'from-purple-500 to-purple-600',
    amber: 'from-amber-500 to-amber-600',
    red: 'from-red-500 to-red-600'
  };
  
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-5 hover:shadow-md transition-all group">
      <div className={`w-10 h-10 rounded-lg bg-gradient-to-br ${colorClasses[color]} flex items-center justify-center mb-3 group-hover:scale-110 transition-transform`}>
        <Icon className="h-5 w-5 text-white" />
      </div>
      <p className="text-3xl font-bold text-slate-900">{value?.toLocaleString() || 0}</p>
      <p className="text-sm text-slate-500">{label}</p>
    </div>
  );
};

export const ElectionsPage = () => {
  const { user, token } = useAuth();
  const [activeTab, setActiveTab] = useState('mps');
  const [councils, setCouncils] = useState([]);
  const [mps, setMps] = useState([]);
  const [mpStats, setMpStats] = useState(null);
  const [elections, setElections] = useState([]);
  const [byElections, setByElections] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCouncil, setSelectedCouncil] = useState(null);
  const [initializing, setInitializing] = useState(false);
  const [filterRegion, setFilterRegion] = useState('');
  const [filterParty, setFilterParty] = useState('');
  
  // Councillor search state
  const [councillorSearch, setCouncillorSearch] = useState('');
  const [councillorResults, setCouncillorResults] = useState([]);
  const [councillorLoading, setCouncillorLoading] = useState(false);
  const [councillorTotal, setCouncillorTotal] = useState(0);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [councilsRes, electionsRes, statsRes, mpsRes, byElectionsRes] = await Promise.all([
        axios.get(`${API}/councils`),
        axios.get(`${API}/elections`),
        axios.get(`${API}/elections/stats`),
        axios.get(`${API}/mps`),
        axios.get(`${API}/elections/by-elections`)
      ]);
      
      // Sort councils alphabetically
      const sortedCouncils = (councilsRes.data.councils || []).sort((a, b) => 
        a.name.localeCompare(b.name)
      );
      setCouncils(sortedCouncils);
      
      setElections(electionsRes.data);
      setStats(statsRes.data);
      
      // Sort MPs alphabetically
      const sortedMps = (mpsRes.data.mps || []).sort((a, b) => 
        a.name.localeCompare(b.name)
      );
      setMps(sortedMps);
      setMpStats(mpsRes.data);
      
      setByElections(byElectionsRes.data.by_elections || []);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load elections data');
    } finally {
      setLoading(false);
    }
  };

  // Councillor search function
  const searchCouncillors = async (query) => {
    if (!query || query.length < 2) {
      setCouncillorResults([]);
      setCouncillorTotal(0);
      return;
    }
    
    setCouncillorLoading(true);
    try {
      const response = await axios.get(`${API}/councillors`, {
        params: { search: query, limit: 50 }
      });
      setCouncillorResults(response.data.councillors || []);
      setCouncillorTotal(response.data.total || 0);
    } catch (error) {
      console.error('Error searching councillors:', error);
      toast.error('Failed to search councillors');
    } finally {
      setCouncillorLoading(false);
    }
  };

  // Debounced councillor search
  useEffect(() => {
    const timer = setTimeout(() => {
      if (activeTab === 'councillors') {
        searchCouncillors(councillorSearch);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [councillorSearch, activeTab]);

  // Get unique regions and parties, sorted alphabetically
  const regions = useMemo(() => {
    const allRegions = new Set();
    councils.forEach(c => allRegions.add(c.region));
    mps.forEach(m => allRegions.add(m.region));
    return Array.from(allRegions).sort();
  }, [councils, mps]);

  const parties = useMemo(() => {
    const allParties = new Set();
    mps.forEach(m => allParties.add(m.party));
    return Array.from(allParties).sort();
  }, [mps]);

  // Filter functions
  const filteredCouncils = useMemo(() => {
    return councils.filter(c => {
      const matchesSearch = c.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        c.region.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesRegion = !filterRegion || c.region === filterRegion;
      return matchesSearch && matchesRegion;
    });
  }, [councils, searchQuery, filterRegion]);

  const filteredMps = useMemo(() => {
    return mps.filter(m => {
      const matchesSearch = m.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        m.constituency.toLowerCase().includes(searchQuery.toLowerCase()) ||
        m.region.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesRegion = !filterRegion || m.region === filterRegion;
      const matchesParty = !filterParty || m.party === filterParty;
      return matchesSearch && matchesRegion && matchesParty;
    });
  }, [mps, searchQuery, filterRegion, filterParty]);

  // Calculate days until key elections
  const localElectionDate = stats?.election_countdowns?.local_elections?.date 
    ? new Date(stats.election_countdowns.local_elections.date) 
    : new Date('2026-05-07');
  const generalElectionDate = stats?.election_countdowns?.general_election?.date 
    ? new Date(stats.election_countdowns.general_election.date) 
    : new Date('2029-05-03');
  
  const daysUntilLocal = Math.ceil((localElectionDate - new Date()) / (1000 * 60 * 60 * 24));
  const daysUntilGeneral = Math.ceil((generalElectionDate - new Date()) / (1000 * 60 * 60 * 24));

  const handleInitialize = async () => {
    if (!user?.is_admin) {
      toast.error('Admin access required');
      return;
    }
    
    setInitializing(true);
    try {
      const res = await axios.post(`${API}/councils/initialize`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(res.data.message);
      fetchData();
    } catch (error) {
      toast.error('Failed to initialize data');
    } finally {
      setInitializing(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center">
          <RefreshCw className="h-8 w-8 animate-spin text-blue-500 mx-auto mb-4" />
          <p className="text-slate-600">Loading elections data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white pb-12" data-testid="elections-page">
      <div className="max-w-7xl mx-auto px-4 pt-8">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-4xl font-bold text-slate-900 mb-2">Elections & Councils</h1>
          <p className="text-slate-600">Track local councils, councillors, MPs, and upcoming elections across the UK</p>
        </div>

        {/* Breaking News Ticker */}
        <BreakingNewsTicker byElections={byElections} />

        {/* Election Countdown Banners */}
        <div className="grid md:grid-cols-2 gap-4 mb-8">
          {/* Local Elections Countdown */}
          <div className="bg-gradient-to-br from-blue-600 via-blue-700 to-blue-800 text-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-shadow relative overflow-hidden">
            <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -mr-16 -mt-16" />
            <div className="absolute bottom-0 left-0 w-24 h-24 bg-white/5 rounded-full -ml-12 -mb-12" />
            <div className="relative flex flex-col sm:flex-row justify-between items-center gap-4">
              <div>
                <p className="text-blue-200 text-xs uppercase tracking-widest font-medium">Next Local Elections</p>
                <h2 className="text-2xl font-bold mt-1">UK Local Elections 2026</h2>
                <p className="text-blue-200 text-sm">Thursday, 7th May 2026</p>
              </div>
              <div className="text-center bg-white/10 backdrop-blur rounded-xl px-6 py-3">
                <p className="text-5xl font-bold" data-testid="local-election-countdown">{daysUntilLocal > 0 ? daysUntilLocal : 0}</p>
                <p className="text-blue-200 text-sm">days to go</p>
              </div>
            </div>
          </div>

          {/* General Election Countdown */}
          <div className="bg-gradient-to-br from-slate-700 via-slate-800 to-slate-900 text-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-shadow relative overflow-hidden">
            <div className="absolute top-0 right-0 w-32 h-32 bg-white/5 rounded-full -mr-16 -mt-16" />
            <div className="absolute bottom-0 left-0 w-24 h-24 bg-white/5 rounded-full -ml-12 -mb-12" />
            <div className="relative flex flex-col sm:flex-row justify-between items-center gap-4">
              <div>
                <p className="text-slate-400 text-xs uppercase tracking-widest font-medium">Next General Election</p>
                <h2 className="text-2xl font-bold mt-1">UK General Election 2029</h2>
                <p className="text-slate-400 text-sm">Expected May 2029</p>
              </div>
              <div className="text-center bg-white/10 backdrop-blur rounded-xl px-6 py-3">
                <p className="text-5xl font-bold" data-testid="general-election-countdown">{daysUntilGeneral > 0 ? daysUntilGeneral : 0}</p>
                <p className="text-slate-400 text-sm">days to go</p>
              </div>
            </div>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-5 gap-4 mb-8">
          <StatCard icon={User} value={stats?.total_mps || mps.length} label="MPs" color="blue" />
          <StatCard icon={Building2} value={stats?.total_councils} label="Councils" color="green" />
          <StatCard icon={Users} value={stats?.total_councillors} label="Councillors" color="purple" />
          <StatCard icon={Calendar} value={2} label="Major Elections" color="amber" />
          <StatCard icon={Bell} value={byElections.length} label="By-Elections" color="red" />
        </div>

        {/* Tabs */}
        <div className="flex flex-wrap gap-2 mb-6 border-b border-slate-200 pb-4">
          {[
            { id: 'mps', label: 'MPs', count: mps.length, icon: User },
            { id: 'councils', label: 'Councils', count: councils.length, icon: Building2 },
            { id: 'councillors', label: 'Find Councillors', icon: Search },
            { id: 'by-elections', label: 'By-Elections', count: byElections.length, icon: Vote },
            { id: 'elections', label: 'Elections', icon: Calendar }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              data-testid={`tab-${tab.id}`}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all ${
                activeTab === tab.id
                  ? 'bg-slate-900 text-white shadow-md'
                  : 'bg-white text-slate-600 hover:bg-slate-100 border border-slate-200'
              }`}
            >
              <tab.icon className="h-4 w-4" />
              {tab.label}
              {tab.count !== undefined && (
                <span className={`text-xs px-2 py-0.5 rounded-full ${
                  activeTab === tab.id ? 'bg-white/20' : 'bg-slate-100'
                }`}>
                  {tab.count}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* Search and Filters */}
        <div className="bg-white rounded-xl border border-slate-200 p-4 mb-6">
          <div className="flex flex-col gap-3">
            <div className="w-full">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  placeholder={activeTab === 'mps' ? 'Search MPs by name or constituency...' : 'Search councils by name or region...'}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                  data-testid="search-input"
                />
              </div>
            </div>
            
            {/* Filter Dropdowns - Mobile Friendly */}
            <div className="flex gap-3 justify-center sm:justify-end">
              {/* Region Filter */}
              <div className="flex-1 sm:flex-none sm:w-40">
                <MobileDropdown
                  value={filterRegion}
                  onChange={setFilterRegion}
                  placeholder="All Regions"
                  label="Select Region"
                  options={regions.map(region => ({ value: region, label: region }))}
                />
              </div>
              
              {/* Party Filter (only for MPs tab) */}
              {activeTab === 'mps' && (
                <div className="flex-1 sm:flex-none sm:w-40">
                  <MobileDropdown
                    value={filterParty}
                    onChange={setFilterParty}
                    placeholder="All Parties"
                    label="Select Party"
                    options={parties.map(party => ({ 
                      value: party, 
                      label: PARTY_COLORS[party]?.label || party,
                      color: PARTY_COLORS[party]?.bg || 'bg-slate-400'
                    }))}
                  />
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Party Breakdown Bar (for MPs and Councils tabs) */}
        {(activeTab === 'mps' || activeTab === 'councils') && (
          <div className="bg-white rounded-xl border border-slate-200 p-6 mb-6">
            <h3 className="font-semibold mb-4">
              {activeTab === 'mps' ? 'MPs by Party' : 'Councillors by Party'}
            </h3>
            
            {/* Animated Progress Bar */}
            <div className="h-6 rounded-full overflow-hidden flex shadow-inner bg-slate-100 mb-4">
              {Object.entries(activeTab === 'mps' ? (mpStats?.party_breakdown || {}) : (stats?.party_breakdown || {}))
                .sort((a, b) => b[1] - a[1])
                .map(([party, count], index) => {
                  const total = Object.values(activeTab === 'mps' ? (mpStats?.party_breakdown || {}) : (stats?.party_breakdown || {}))
                    .reduce((a, b) => a + b, 0);
                  const percentage = (count / total) * 100;
                  const colors = PARTY_COLORS[party] || PARTY_COLORS.other;
                  return (
                    <div 
                      key={party}
                      className={`${colors.bg} transition-all duration-1000 ease-out hover:brightness-110 cursor-pointer`}
                      style={{ 
                        width: `${percentage}%`,
                        animationDelay: `${index * 100}ms`
                      }}
                      title={`${colors.label}: ${count} (${percentage.toFixed(1)}%)`}
                    />
                  );
                })}
            </div>
            
            {/* Legend */}
            <div className="flex flex-wrap gap-4">
              {Object.entries(activeTab === 'mps' ? (mpStats?.party_breakdown || {}) : (stats?.party_breakdown || {}))
                .sort((a, b) => b[1] - a[1])
                .map(([party, count]) => (
                  <PartyBadge key={party} party={party} count={count} />
                ))}
            </div>
          </div>
        )}

        {/* Content based on active tab */}
        {activeTab === 'mps' && (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredMps.slice(0, 60).map((mp, index) => (
              <MPCard key={`${mp.name}-${mp.constituency}-${index}`} mp={mp} />
            ))}
            {filteredMps.length > 60 && (
              <div className="col-span-full text-center py-4">
                <p className="text-slate-500">Showing 60 of {filteredMps.length} MPs. Use search to find specific MPs.</p>
              </div>
            )}
          </div>
        )}

        {activeTab === 'councils' && (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredCouncils.slice(0, 30).map((council) => (
              <CouncilCard key={council.id} council={council} />
            ))}
            {filteredCouncils.length > 30 && (
              <div className="col-span-full text-center py-4">
                <p className="text-slate-500">Showing 30 of {filteredCouncils.length} councils. Use search to find specific councils.</p>
              </div>
            )}
          </div>
        )}

        {activeTab === 'councillors' && (
          <div className="space-y-6">
            {/* Councillor Search Box */}
            <div className="bg-gradient-to-r from-purple-50 to-blue-50 rounded-2xl p-6 border border-purple-100">
              <div className="max-w-2xl mx-auto text-center mb-6">
                <h3 className="text-2xl font-bold text-slate-900 mb-2">Find Your Local Councillor</h3>
                <p className="text-slate-600">Search by councillor name, council name, or ward</p>
              </div>
              <div className="max-w-xl mx-auto">
                <div className="relative">
                  <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
                  <Input
                    placeholder="Search councillors by name, council, or ward..."
                    value={councillorSearch}
                    onChange={(e) => setCouncillorSearch(e.target.value)}
                    className="pl-12 py-6 text-lg rounded-xl shadow-sm"
                    data-testid="councillor-search-input"
                  />
                  {councillorLoading && (
                    <div className="absolute right-4 top-1/2 -translate-y-1/2">
                      <RefreshCw className="h-5 w-5 animate-spin text-purple-500" />
                    </div>
                  )}
                </div>
                {councillorSearch.length > 0 && councillorSearch.length < 2 && (
                  <p className="text-sm text-slate-500 mt-2 text-center">Type at least 2 characters to search</p>
                )}
              </div>
            </div>

            {/* Search Results */}
            {councillorResults.length > 0 && (
              <div>
                <div className="flex items-center justify-between mb-4">
                  <p className="text-slate-600">
                    Found <span className="font-bold text-slate-900">{councillorTotal.toLocaleString()}</span> councillors
                    {councillorTotal > 50 && <span className="text-slate-500"> (showing first 50)</span>}
                  </p>
                </div>
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {councillorResults.map((councillor, index) => (
                    <CouncillorCard key={councillor.id || index} councillor={councillor} />
                  ))}
                </div>
              </div>
            )}

            {/* No Results */}
            {councillorSearch.length >= 2 && councillorResults.length === 0 && !councillorLoading && (
              <div className="text-center py-12 text-slate-500">
                <Users className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>No councillors found matching "{councillorSearch}"</p>
                <p className="text-sm mt-2">Try searching by name, council, or ward</p>
              </div>
            )}

            {/* Empty State */}
            {councillorSearch.length === 0 && (
              <div className="text-center py-12">
                <div className="bg-slate-100 rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-4">
                  <Search className="h-10 w-10 text-slate-400" />
                </div>
                <p className="text-slate-600 text-lg">Search from <span className="font-bold">{stats?.total_councillors?.toLocaleString() || '19,107'}</span> councillors</p>
                <p className="text-slate-500 text-sm mt-2">Covering all {stats?.total_councils || 382} UK councils</p>
              </div>
            )}
          </div>
        )}

        {activeTab === 'by-elections' && (
          <div>
            {/* Parliamentary By-Elections Section */}
            {byElections.some(be => be.election_type === 'parliamentary') && (
              <div className="mb-8">
                <h3 className="text-xl font-bold text-slate-900 mb-4 flex items-center gap-2">
                  <Landmark className="h-5 w-5 text-purple-500" />
                  Parliamentary By-Elections
                </h3>
                <div className="grid md:grid-cols-2 gap-4">
                  {byElections
                    .filter(be => be.election_type === 'parliamentary')
                    .map((election, index) => (
                      <ByElectionCard key={`parliamentary-${index}`} election={election} />
                    ))}
                </div>
              </div>
            )}
            
            {/* Local By-Elections Section */}
            <div>
              <h3 className="text-xl font-bold text-slate-900 mb-4 flex items-center gap-2">
                <Building2 className="h-5 w-5 text-amber-500" />
                Local Council By-Elections
              </h3>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {byElections
                  .filter(be => be.election_type !== 'parliamentary')
                  .map((election, index) => (
                    <ByElectionCard key={`local-${index}`} election={election} />
                  ))}
              </div>
            </div>
            
            {byElections.length === 0 && (
              <div className="text-center py-12 text-slate-500">
                <Calendar className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>No upcoming by-elections scheduled</p>
              </div>
            )}
          </div>
        )}

        {activeTab === 'elections' && (
          <div className="space-y-6">
            {/* Major Elections */}
            <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-2xl p-6 border border-blue-100">
              <h3 className="text-xl font-bold text-slate-900 mb-4">Scheduled Major Elections</h3>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="bg-white rounded-xl p-5 shadow-sm">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 rounded-lg bg-blue-500 flex items-center justify-center">
                      <Building2 className="h-5 w-5 text-white" />
                    </div>
                    <div>
                      <h4 className="font-semibold">UK Local Elections 2026</h4>
                      <p className="text-sm text-slate-500">Thursday, 7th May 2026</p>
                    </div>
                  </div>
                  <p className="text-sm text-slate-600">Local council elections across England. All 385 councils will hold elections.</p>
                </div>
                
                <div className="bg-white rounded-xl p-5 shadow-sm">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 rounded-lg bg-slate-700 flex items-center justify-center">
                      <Landmark className="h-5 w-5 text-white" />
                    </div>
                    <div>
                      <h4 className="font-semibold">UK General Election 2029</h4>
                      <p className="text-sm text-slate-500">Expected May 2029</p>
                    </div>
                  </div>
                  <p className="text-sm text-slate-600">UK General Election for all 650 parliamentary constituencies.</p>
                </div>
              </div>
            </div>
            
            {/* Admin Initialize */}
            {user?.is_admin && (
              <div className="bg-slate-100 rounded-xl p-6">
                <h3 className="font-semibold mb-4">Admin Controls</h3>
                <Button 
                  onClick={handleInitialize}
                  disabled={initializing}
                  className="bg-slate-900 hover:bg-slate-800"
                >
                  {initializing ? (
                    <><RefreshCw className="h-4 w-4 mr-2 animate-spin" /> Initializing...</>
                  ) : (
                    <><RefreshCw className="h-4 w-4 mr-2" /> Reinitialize Database</>
                  )}
                </Button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ElectionsPage;
